import { GET_DEFAULT_CAR_ITEM_SPECIFICATIONS } from '@/constants/car';
import splitCompositeFilterPath from '@/features/filters/helpers/splitCompositeFilterPath';
import {
  CarItemSpecificationItemsType,
  CarSpecificationItemValueType,
  ICarSpecificationItem,
} from '@/interfaces/car.interfaces';
import {
  ExtendedCarItemType,
  IExtendedCollectionData,
  IExtendedSpecificationValue,
} from '@/interfaces/cars.interfaces';
import {
  NullableType,
  PrimitiveType,
  RequiredPropertyType,
} from '@/interfaces/common.interfaces';
import { II18Service } from '@/services/I18Service';
import InjectContainer from '@/services/InjectContainer';
import ServiceTypes from '@/services/ServiceTypes';
import formatData from '@/utils/formatData';
import instanceOfReference from '@/utils/instanceOfReference';
import isDefined from '@/utils/isDefined';

export type CarFormattedSpecificationItemType = RequiredPropertyType<
  Pick<ICarSpecificationItem, 'id' | 'icon' | 'label' | 'value'>,
  'value'
>;

export const getCarSpecification = (
  extendedCarData: ExtendedCarItemType,
  item: ICarSpecificationItem,
): NullableType<CarFormattedSpecificationItemType> => {
  let resultItem: NullableType<CarFormattedSpecificationItemType> = null;

  const { id, key: itemKey, path: itemPath, label, icon, value, formatter } = item;

  const resolvedPath = itemPath ?? itemKey;

  if (!resolvedPath) {
    return resultItem;
  }

  const isMultiple = Array.isArray(resolvedPath);

  const compositePaths = isMultiple ? resolvedPath : [resolvedPath];
  const values: NullableType<CarSpecificationItemValueType>[] = Array(
    compositePaths.length,
  ).fill(null);

  for (let i = 0; i < compositePaths.length; i++) {
    const compositePath = compositePaths[i];
    const { path, field } = splitCompositeFilterPath(compositePath);

    const carFieldValue = extendedCarData.data?.[path];

    let resolvedValue: NullableType<PrimitiveType> = null;

    if (isDefined(value)) {
      resolvedValue = value;
    } else if (
      (Array.isArray(carFieldValue) && instanceOfReference(carFieldValue[0])) ||
      instanceOfReference(carFieldValue)
    ) {
      const extendedItems = (
        Array.isArray(carFieldValue) ? carFieldValue : [carFieldValue]
      ) as IExtendedCollectionData[];

      const extendedItem = extendedItems.find(value => {
        if (!field) {
          return true;
        }

        return value?.[field.key] === field.value;
      });

      if (!extendedItem) {
        continue;
      }

      resolvedValue = extendedItem.displayName ?? extendedItem.name ?? null;

      if ('specification' in extendedItem) {
        const specificationItem = extendedItem as IExtendedSpecificationValue;

        if (typeof specificationItem.stringValue === 'string') {
          resolvedValue ??= specificationItem.stringValue;
        } else if (typeof specificationItem.numberValue === 'number') {
          resolvedValue ??= specificationItem.numberValue;
        } else if (typeof specificationItem.booleanValue === 'boolean') {
          resolvedValue ??= specificationItem.booleanValue;
        }
      }
    } else if (isDefined(carFieldValue)) {
      resolvedValue = carFieldValue as PrimitiveType;
    }

    if (typeof resolvedValue === 'boolean') {
      const { i18n } = InjectContainer.get<II18Service>(ServiceTypes.I18N);

      resolvedValue = resolvedValue
        ? i18n.t('booleanValues.trueValue')
        : i18n.t('booleanValues.falseValue');
    }

    if (!isDefined(resolvedValue)) {
      continue;
    }

    values[i] = resolvedValue;
  }

  if (values.every(value => !isDefined(value))) {
    return resultItem;
  }

  if (!isMultiple) {
    resultItem = {
      id,
      icon,
      label,
      value: formatData(values[0] as CarSpecificationItemValueType, formatter),
    };
  } else {
    resultItem = { id, icon, label, value: formatData(values, formatter) };
  }

  return resultItem;
};

const getCarSpecifications = (
  extendedCarData: ExtendedCarItemType,
  items: CarItemSpecificationItemsType = GET_DEFAULT_CAR_ITEM_SPECIFICATIONS(),
): CarFormattedSpecificationItemType[] => {
  const result: CarFormattedSpecificationItemType[] = [];

  for (const item of items) {
    const resolvedItem = getCarSpecification(extendedCarData, item);

    if (isDefined(resolvedItem)) {
      result.push(resolvedItem);
    }
  }

  return result;
};

export default getCarSpecifications;
